import type React from "react";

import { useAuth } from "@/contexts/auth";
import { errorHandler } from "@/lib/utils";
import { authService } from "@/services/api";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import logo from "@/assets/logo-canto-verde.ico";

export function Login() {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    try {
      const response = await authService.login(login, password);

      authLogin(response.data);

      const redirectUrl = response.data.redirect_url;
      // A rota do módulo pode vir com barra inicial ("/dashboard"); somada à
      // barra abaixo vira "//dashboard", que o react-router trata como URL
      // externa e rejeita sem navegar.
      const url =
        typeof redirectUrl === "string"
          ? redirectUrl.trim().replace(/^\/+/, "")
          : "";

      navigate("/" + url);
    } catch (err) {
      errorHandler(err, "Credenciais inválidas");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="space-y-6">
      <div>
        <img
          src={logo || "/placeholder.svg"}
          alt="Logo Verde"
          className="mx-auto mb-6 w-32 h-32"
        />
        <h1 className="text-center text-2xl font-bold text-gray-900">
          Acesse sua conta
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 login-form">
        {error && (
          <div className="p-3 text-sm text-red-500 bg-red-50 rounded">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="login"
            className="block text-sm font-medium text-gray-700"
          >
            Usuário
          </label>
          <input
            id="login"
            type="text"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            required
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700"
          >
            Senha
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 pr-10"
              required
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-0 mt-1 flex items-center pr-3 text-gray-600 hover:text-gray-900"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
              <span className="sr-only">
                {showPassword ? "Esconder senha" : "Mostrar senha"}
              </span>
            </button>
          </div>
        </div>

        <button
          type="submit"
          className="w-full py-2 px-4 bg-green-700 text-white rounded-md hover:bg-green-800 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
        >
          Entrar
        </button>
      </form>

      <div className="text-center">
        <a
          href="/auth/forgot-password"
          className="text-sm text-primary hover:underline"
        >
          Esqueceu sua senha?
        </a>
      </div>
    </div>
  );
}
